import { Serializer } from 'common/serializer';
import { Map } from 'immutable';

import { Unauthorized } from 'common/messages/unauthorized';

import { WorkItemViewInfo } from 'runtime/data/workitemviewinfo';
import { WorkItem } from 'runtime/data/workitem';
import { FieldDataUpdate } from 'runtime/data/fielddataupdate';
import { FieldDataSecurity } from 'runtime/data/fielddatasecurity';
import { RuleDataUpdate } from 'runtime/data/ruledataupdate';
import { SessionDataUpdate } from 'runtime/data/sessiondataupdate';
import { SaveResult } from 'runtime/data/saveresult';
import { SubmitResult } from 'runtime/data/submitresult';
import { LookupItem } from 'runtime/data/lookupitem';
import { LookupPropertyBoolean, LookupPropertyDateTime, LookupPropertyDecimal, LookupPropertyString } from 'runtime/data/lookupproperty';
import { FieldIdentifier } from 'runtime/data/fieldidentifier';
import { FileFieldMetadata } from 'runtime/data/filefieldmetadata';
import { UploadResult } from 'runtime/data/uploadresult';
import { ActivityUpdate } from 'runtime/data/activityupdate';
import { ActivityReturnValue } from 'runtime/data/activityreturnvalue';
import { LookupItemUpdate } from 'runtime/data/lookupitemupdate';
import { WorkflowDefinition } from 'runtime/data/workflowdefinition';
import { WorkTrayData } from 'runtime/data/worktraydata';
import { WorkTrayView } from 'runtime/data/worktrayview';
import { WorkTrayColumn } from 'runtime/data/worktraycolumn';
import { Condition, Offset, WorkItemStates, ConditionOfWorkItemState, ConditionOfWorkItemStates, ConditionOfString, ConditionOfDateTime, ConditionOfInt, ConditionOfInts, ConditionOfTimeSpan } from 'runtime/data/condition';
import { WorkTrayPage } from 'runtime/data/worktraypage';
import { WorkItemInfo } from 'runtime/data/workiteminfo';
import { UserInfo } from 'runtime/data/userinfo';
import { WorkTrayViewInfo } from 'runtime/data/worktrayviewinfo';
import { HistoryInfoArrived, HistoryInfoAssigned, HistoryInfoClaimCancelled, HistoryInfoClaimed, HistoryInfoClaimForceCancelled, HistoryInfoCreated, HistoryInfoSubmitted, HistoryInfoTransferred } from 'runtime/data/historyInfo';
import { RuntimeInfo, RuntimeCustomControlInfo } from 'runtime/data/runtimeinfo';

import { StringResult } from 'common/stringresult';
import { BooleanResult } from 'common/booleanresult';
import { ValidationSummary, ValidationResult, ValidationFailed, ValidationSummaryAndDataChanges, WorkItemValidationFailed } from 'shared/data/validation';
import { ConcurrencyCheckFailed } from 'shared/messages/concurrencycheckfailed';

import { WorkflowInstanceInfo } from 'runtime/data/workflowinstanceinfo';
import { WorkflowInstanceInfoPage } from 'runtime/data/workflowinstanceinfopage';

import { SearchResult } from 'runtime/data/searchresult';
import { SearchResultPage } from 'runtime/data/searchresultpage';
import { UnsupportedDate, UnsupportedWildcardLocation} from 'runtime/messages/workitemsearch';

import { User } from 'shared/data/user';
import { UserDelegation } from 'runtime/data/userdelegation';
import { CurrentUserDelegation } from 'runtime/data/currentuserdelegation';
import { BulkActionTarget } from 'runtime/data/bulkactiontarget';

import { AmbiguousDataVersion } from 'runtime/messages/ambiguousdataversion';
import { WorkItemNotAvailableForClaim } from 'runtime/messages/workitemnotavailableforclaim';
import { WorkItemNotFound } from 'runtime/messages/workitemnotfound';
import { WorkItemDiscarded } from 'runtime/messages/workitemdiscarded';
import { WorkItemSecurityViolation } from 'runtime/messages/workitemsecurityviolation';
import { WorkTrayViewNotFound } from 'runtime/messages/worktrayviewnotfound';

import { SessionError } from 'runtime/messages/sessionerror';

import { BadFileFormat } from 'runtime/messages/badfileformat';
import { VirusDetected, InfectedFile } from 'runtime/messages/virusdetected';

import { SentNotificationInfo, UnsentNotificationInfo } from 'runtime/data/notificationinfo';

import { SystemError } from 'common/exceptions/systemerror';
import { AccessDenied } from 'common/exceptions/accessdenied';

import { RenewingTokenFailed } from 'shared/messages/renewingTokenFailed'
import { NotificationProcessingResult } from 'runtime/messages/notificationprocessingresult';
import { BulkActionInfo } from 'runtime/data/bulkactioninfo';

export function createSerializer() {
    const typeRegistry = Map<string, typeof Object>().withMutations(map => {
        const register = (type: any, name: string) => map.set(name, type);

        register(Unauthorized, 'Web.Unauthorized');

        register(WorkItemViewInfo, 'Web.WorkItemViewInfo');
        register(WorkItem, 'Core.WorkItem');
        register(FieldDataUpdate, 'Web.FieldDataUpdate');
        register(FieldDataSecurity, 'Web.FieldDataSecurity');
        register(RuleDataUpdate, 'Web.RuleDataUpdate');
        register(SessionDataUpdate, 'Web.SessionDataUpdate');
        register(SaveResult, 'Web.SaveResult');
        register(SubmitResult, 'Web.SubmitResult');
        register(LookupItem, 'Core.LookupItem');
        register(LookupPropertyBoolean, 'Core.LookupPropertyBoolean');
        register(LookupPropertyDateTime, 'Core.LookupPropertyDateTime');
        register(LookupPropertyDecimal, 'Core.LookupPropertyDecimal');
        register(LookupPropertyString, 'Core.LookupPropertyString');
        register(LookupItemUpdate, 'Web.LookupItemUpdate');
        register(FieldIdentifier, 'Web.FieldIdentifier');
        register(FileFieldMetadata, 'Web.FileFieldMetadata');
        register(UploadResult, 'Web.UploadResult');
        register(ActivityUpdate, 'Web.ActivityUpdate');
        register(ActivityReturnValue, 'Core.ActivityReturnValue');
        register(WorkflowDefinition, 'Core.WorkflowDefinition');
        register(WorkTrayData, 'Web.WorkTrayData');
        register(WorkTrayView, 'Core.SystemWorkTrayView');
        register(WorkTrayView, 'Core.WorkTrayView');
        register(WorkTrayColumn, 'Core.WorkTrayColumn');

        register(Condition, 'Core.Condition');
        register(ConditionOfWorkItemState, 'Core.ConditionOfWorkItemState');
        register(ConditionOfWorkItemStates, 'Core.ConditionOfWorkItemStates');
        register(ConditionOfString, 'Core.ConditionOfString');
        register(ConditionOfDateTime, 'Core.ConditionOfDateTime');
        register(ConditionOfTimeSpan, 'Core.ConditionOfTimeSpan');
        register(Offset, 'Core.Offset');
        register(ConditionOfInt, 'Core.ConditionOfInt');
        register(ConditionOfInts, 'Core.ConditionOfInts');

        register(WorkTrayPage, 'Web.WorkTrayPage');
        register(WorkItemInfo, 'Core.WorkItemInfo');
        register(UserInfo, 'Core.UserInfo');
        register(WorkTrayViewInfo, 'Web.WorkTrayViewInfo');
        register(StringResult, 'Web.StringResult');
        register(BooleanResult, 'Web.BooleanResult');
        register(ValidationSummary, 'Core.ValidationSummary');
        register(ValidationResult, 'Core.ValidationResult');
        register(ValidationFailed, 'Core.ValidationFailed');
        register(ValidationSummaryAndDataChanges, 'Web.ValidationSummaryAndDataChanges');
        register(WorkItemValidationFailed, 'Web.WorkItemValidationFailedWithDataChanges');

        register(HistoryInfoArrived, 'Core.HistoryInfoArrived');
        register(HistoryInfoAssigned, 'Core.HistoryInfoAssigned');
        register(HistoryInfoClaimCancelled, 'Core.HistoryInfoClaimCancelled');
        register(HistoryInfoClaimed, 'Core.HistoryInfoClaimed');
        register(HistoryInfoClaimForceCancelled, 'Core.HistoryInfoClaimForceCancelled');
        register(HistoryInfoCreated, 'Core.HistoryInfoCreated');
        register(HistoryInfoSubmitted, 'Core.HistoryInfoSubmitted');
        register(HistoryInfoTransferred, 'Core.HistoryInfoTransferred');

        register(WorkflowInstanceInfo, 'Core.WorkflowInstanceInfo');
        register(WorkflowInstanceInfoPage, 'Web.WorkflowInstanceInfoPage');

        register(SearchResult, 'Core.SearchResult');
        register(SearchResultPage, 'Web.SearchResultPage');
        register(UnsupportedDate, 'WorkItemSearch.UnsupportedDate');
        register(UnsupportedWildcardLocation, 'WorkItemSearch.UnsupportedWildcardLocation');
        
        register(User, 'Core.User');
        register(UserDelegation, 'Core.UserDelegation');
        register(CurrentUserDelegation, 'Web.CurrentUserDelegation');

        register(AmbiguousDataVersion, 'Core.AmbiguousDataVersion');

        register(WorkItemNotAvailableForClaim, 'Core.WorkItemNotAvailableForClaim');
        register(WorkItemSecurityViolation, 'Core.WorkItemSecurityViolation');

        register(BulkActionInfo, 'Core.BulkActionInfo');
        register(BulkActionTarget, 'Core.BulkActionTarget');

        register(SessionError, 'Web.SessionError');

        register(ConcurrencyCheckFailed, 'Core.ConcurrencyCheckFailed');

        register(WorkItemNotFound, 'Core.WorkItemNotFound');
        register(WorkItemDiscarded, 'Core.WorkItemDiscarded');
        register(WorkTrayViewNotFound, 'Core.WorkTrayViewNotFound');

        register(RenewingTokenFailed, 'SAM.Workflow.Core.ExceptionMessages.RenewingTokenFailed');

        register(WorkItemNotFound, 'SAM.Workflow.Core.ExceptionMessages.WorkItemNotFound');
        register(WorkItemDiscarded, 'SAM.Workflow.Core.ExceptionMessages.WorkItemDiscarded');
        register(WorkTrayViewNotFound, 'SAM.Workflow.Core.ExceptionMessages.WorkTrayViewNotFound');
        register(SystemError, 'Core.SystemError');
        register(AccessDenied, 'Core.AccessDenied');

        register(BadFileFormat, 'Core.BadFileFormat');
        register(VirusDetected, 'Core.VirusDetected');
        register(InfectedFile, 'Core.InfectedFile');

        register(RuntimeInfo, 'Web.RuntimeInfo');
        register(RuntimeCustomControlInfo, 'Web.RuntimeCustomControlInfo');

        register(SentNotificationInfo, 'Web.SentNotificationInfo');
        register(UnsentNotificationInfo, 'Web.UnsentNotificationInfo');

        register(NotificationProcessingResult, 'Web.NotificationProcessingResult');
        
    });

    return new Serializer(typeRegistry);
}